import moment from "moment";
import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Field, formValueSelector } from "redux-form";
import { ADMINISTRATOR, DIRECTOR, FINTECH } from "../../../../../utils/Roles";
import userHasAccess from "../../../../../utils/userHasAccess";
import { DayPicker, StyledCheckboxInput } from "../../../../common/form";
import { ACTIVE } from "../../enums/clientStatusTypes";
import { GM } from "../../enums/businessAccountTypes";
import { MessageBox } from "../../../../common";
import { getTenantFromURL } from "../../../../common/MultiTenancy/MultiTenancyHelper";

let startDateValidation;

class CoveragePeriod extends React.Component {
  constructor(props) {
    super(props);
    this.showButton = this.showButton.bind(this);
    this.checkDurationWarning = this.checkDurationWarning.bind(this);
    this.state = {
      showDurationWarning: false,
    };
  }
  showButton(event) {
    const changeFieldValue = this.props.changeFieldValue;
    if (event.target.checked) {
      const minimumStartDate = moment(this.props.initialValues.coveragePeriod.endDate, "YYYY-MM-DD").add(1, 'days');
      startDateValidation = (current) => current.isAfter(minimumStartDate);
      changeFieldValue(
        "client",
        "coveragePeriod.startDate",
        minimumStartDate
      );
      changeFieldValue("client", "coveragePeriod.endDate", "");
      // End date is cleared on renewal, so no warning needed
      this.checkDurationWarning(minimumStartDate, "");
    } else {
      startDateValidation = undefined;
      const originalStartDate = this.props.initialValues.coveragePeriod.startDate;
      const originalEndDate = this.props.initialValues.coveragePeriod.endDate;
      changeFieldValue(
        "client",
        "coveragePeriod.startDate",
        originalStartDate
      );
      changeFieldValue(
        "client",
        "coveragePeriod.endDate",
        originalEndDate
      );
      this.setState({ showDurationWarning: false });
      this.props.setHasCoveragePeriodWarning(false);
    }
  }

  /**
   * Checks if the coverage period duration is less than 1 year.
   * Updates state to show/hide warning message accordingly.
   */
  checkDurationWarning(startDate, endDate) {
    // Only show warning if both dates are selected
    if (startDate && endDate) {
      const start = (moment.isMoment(startDate) ? startDate : moment(startDate)).format("YYYY-MM-DD");
      const end = (moment.isMoment(endDate) ? endDate : moment(endDate)).format("YYYY-MM-DD");
      const oneYearDurationDate = moment(start).add(1, "years").format("YYYY-MM-DD");
      const endPlusOneDay = moment(end).add(1, "days").format("YYYY-MM-DD");
      const tenant = getTenantFromURL() && getTenantFromURL().toLowerCase(); 
      // Warning shown only for GM clients when end date + 1 day is strictly before start date + 1 year (end date is inclusive)
      const showWarning = moment(endPlusOneDay).isBefore(moment(oneYearDurationDate)) && tenant == GM.acronym.toLowerCase();
      this.setState({ showDurationWarning: showWarning });
      this.props.setHasCoveragePeriodWarning(showWarning);
    } else {
      this.setState({ showDurationWarning: false });
      this.props.setHasCoveragePeriodWarning(false);
    }
  }
  render() {
    const { disabled, create, clientStatusType, isInactivationPlanned, coveragePeriodStartDate, coveragePeriodEndDate } = this.props;
    const canRenewPeriod = !create && clientStatusType == ACTIVE.value && !isInactivationPlanned;

    /**
     * Handler for when start date changes. Checks duration warning and
     * gets the end date from form values to calculate total duration.
     */
    const handleStartDateChange = (value) => {
      this.checkDurationWarning(value, coveragePeriodEndDate);
    };

    /**
     * Handler for when end date changes. Checks duration warning and
     * gets the start date from form values to calculate total duration.
     */
    const handleEndDateChange = (value) => {
      this.checkDurationWarning(coveragePeriodStartDate, value);
    };

    return (
      <div className="ClientSubsection">
        <h3>
          <FormattedMessage id="clients.form.coveragePeriods.title" />
        </h3>
        <Field
          id="coveragePeriod.startDate"
          name="coveragePeriod.startDate"
          component={DayPicker}
          required={true}
          disabled={disabled}
          label={
            <FormattedMessage id="clients.form.coveragePeriods.startDate" />
          }
          props={{
            isValidDate: startDateValidation,
            onChangeFunc: handleStartDateChange,
          }}
        />
        <Field
          id="coveragePeriod.endDate"
          name="coveragePeriod.endDate"
          component={DayPicker}
          required={true}
          disabled={disabled}
          label={<FormattedMessage id="clients.form.coveragePeriods.endDate" />}
          props={{
            isValidDate: startDateValidation,
            onChangeFunc: handleEndDateChange,
          }}
        />

        {(this.state.showDurationWarning || this.props.hasCoveragePeriodWarning) && (
          <MessageBox key="durationWarning" className="alert-warning">
              <FormattedMessage id="clients.form.coveragePeriods.durationWarning" />
          </MessageBox>
        )}

        {userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR], true) && canRenewPeriod && (
          <Field
            id="isRenewal"
            name="isRenewal"
            type="checkbox"
            component={StyledCheckboxInput}
            disabled={disabled}
            checkbox={{ value: true }}
            props={{ onChange: this.showButton }}
            label={
              <FormattedMessage id="clients.form.coveragePeriods.renewCoverage" />
            }
            normalize={(value) => !!value}
          />
        )}
      </div>
    );
  }
}
CoveragePeriod.propTypes = {
  disabled: PropTypes.bool,
  initialValues: PropTypes.object,
  create: PropTypes.bool,
  changeFieldValue: PropTypes.func,
  clientStatusType: PropTypes.string,
  isInactivationPlanned: PropTypes.bool,
  coveragePeriodStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  coveragePeriodEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hasCoveragePeriodWarning: PropTypes.bool,
  setHasCoveragePeriodWarning: PropTypes.func
};

/**
 * Maps Redux form state to component props to access current form field values.
 * This allows the component to check both dates when either one changes.
 */
function mapStateToProps(state) {
  const selector = formValueSelector("client");
  return {
    coveragePeriodStartDate: selector(state, "coveragePeriod.startDate"),
    coveragePeriodEndDate: selector(state, "coveragePeriod.endDate")
  };
}

export default connect(mapStateToProps)(CoveragePeriod);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client-form/basic-client-sections/CoveragePeriod.js